interface AnimatedCounterOptions {
	useEasing?: boolean;
	duration?: number;
	startValue?: number;
	threshold?: number;
}

export class AnimatedCounter {
	private container: HTMLElement;
	private value: number;
	private startValue: number;
	private duration: number;
	private startTime: number | null = null;
	private rafId: number | null = null;
	private observer: IntersectionObserver | null = null;
	private useEasing: boolean;
	private threshold: number;

	private static readonly selectorVisibleContainer = 'animated-counter--visible';

	constructor(element: HTMLElement, options: AnimatedCounterOptions = {}) {
		if (!element) return;

		this.container = element;
		this.value = parseFloat(this.container.textContent ?? '0') || 0;

		this.useEasing = options.useEasing ?? false;
		this.duration = options.duration ?? 1200;
		this.startValue = options.startValue ?? 1;
		this.threshold = options.threshold ?? 0.3;

		if (this.value <= this.startValue) return;

		this.initObserver();
	}

	private initObserver = (): void => {
		this.observer = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting) {
					this.onVisible();
				}
			},
			{ threshold: this.threshold },
		);

		this.observer.observe(this.container);
	};

	private onVisible = (): void => {
		this.container.classList.add(AnimatedCounter.selectorVisibleContainer);

		this.container.textContent = String(this.startValue);
		this.animateValue();

		this.observer?.unobserve(this.container);
		this.observer = null;
	};

	private animateValue = (): void => {
		const decimalPlaces = this.getDecimalPlaces(this.value);

		const step = (timestamp: number) => {
			if (!this.startTime) this.startTime = timestamp;

			const rawProgress = Math.min((timestamp - this.startTime) / this.duration, 1);
			const progress = this.useEasing ? this.easeOutCubic(rawProgress) : rawProgress;

			const currentValue = this.startValue + (this.value - this.startValue) * progress;

			this.container.textContent = currentValue.toFixed(decimalPlaces);

			if (rawProgress < 1) {
				this.rafId = requestAnimationFrame(step);
			}
		};

		this.rafId = requestAnimationFrame(step);
	};

	private getDecimalPlaces(value: number): number {
		const valueStr = value.toString();
		return valueStr.includes('.') ? valueStr.split('.')[1].length : 0;
	}

	private easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);

	destroy = (): void => {
		if (this.rafId) {
			cancelAnimationFrame(this.rafId);
		}

		this.observer?.disconnect();

		this.container.classList.remove(AnimatedCounter.selectorVisibleContainer);
	};
}
